/**
 * Pure helpers for the imports conversation (routes/twin/imports-copilot.tsx): what a message's status means in
 * words, how the page polls a thread (and when it stops), what the composer refuses before sending, what a refusal
 * from the server says, and the container's decisions: what the conversation shows, which thread a message goes to,
 * what a send came to (sendAndConfirm), what the composer keeps after it (#843 review MED-6: the container is
 * not rendered in tests), and how a message goes up with the page's context (outgoingPost).
 */
import { ApiClientError, NetworkError } from "@/lib/api-client";
import type {
  CopilotAnswerBlock,
  CopilotMessage,
  CopilotPersonBlock,
  CopilotPost,
  CopilotThread,
} from "@/lib/api/copilot-imports";
import { contextBlockOf, type CopilotAbout } from "@/lib/copilot/ask";

export const MAX_MESSAGE_CHARS = 8000;
export const MAX_FILES = 1; // one file per message: the server holds a message's file in memory while it takes it
export const MAX_FILE_MB = 64;
export const FILE_HINT = `one file, up to ${MAX_FILE_MB} MB`;
/** The server lists a thread 500 messages at a time, oldest first. */
export const MESSAGES_PAGE = 500;

/** Questions a new person can start with, above the composer: a click sends one as their message. */
export const COPILOT_STARTERS = [
  "Generate a report",
  "What drives sand inclusion?",
  "Set an alarm on a tag",
  "What data do we have?",
  "How do I add equipment?",
] as const;

/**
 * The fixed starters to show beside the brief's own quick questions: a "What drives ...?" starter about a defect a
 * quick question already asks about is left out, so the page never offers one defect twice in two words ("What drives
 * sand inclusion?" beside the plant's "What drives Dirt / Sand Inclusion?").
 */
export function startersBeside(
  starters: readonly string[],
  asks: readonly { about: CopilotAbout | null }[],
): string[] {
  const covered = new Set(
    asks
      .map((a) => a.about?.defect)
      .filter((d): d is string => typeof d === "string" && d !== "")
      .map((d) => d.replace(/[_-]+/g, " ").trim().toLowerCase()),
  );
  return starters.filter((s) => {
    const m = /^What drives (.+)\?$/i.exec(s);
    return !m || !covered.has(m[1].trim().toLowerCase());
  });
}

/**
 * The starters a conversation shows (2026-10-28): the plant's own (the panel's, made from its brief) only while the
 * thread has no message, and none once a conversation has started; without them, the fixed starters beside the quick
 * questions, as before (startersBeside). An empty list shows no starters at all.
 */
export function startersShown(
  plant: readonly string[] | undefined,
  messages: number,
  asks: readonly { about: CopilotAbout | null }[],
): string[] {
  if (plant) return messages === 0 ? [...plant] : [];
  return startersBeside(COPILOT_STARTERS, asks);
}

/**
 * Where a person's message stands, in words; "" once it is answered (the reply says the rest). A failed message's
 * error is the platform's own sentence (never the brain's text), so it is shown as it is.
 */
export function messageStatusText(m: CopilotMessage): string {
  switch (m.status) {
    case "pending":
      return "Waiting for the consultant";
    case "claimed":
      return "The consultant is answering";
    case "failed":
      return m.error || "The consultant couldn't answer. Send the message again.";
    default:
      return "";
  }
}

function waiting(m: CopilotMessage): boolean {
  return m.status === "pending" || m.status === "claimed";
}

/** True while any message of the thread waits for, or is with, the brain. */
export function awaitingBrain(messages: CopilotMessage[]): boolean {
  return messages.some(waiting);
}

/**
 * Where the next poll of a thread starts (the server's `after=`): after the message before the first one still
 * waiting, so the waiting ones come again with their new status; else after the last one, for anything new. Null:
 * from the start (nothing loaded yet, or the first message itself waits).
 */
export function pollAfter(messages: CopilotMessage[]): string | null {
  const i = messages.findIndex(waiting);
  if (i === -1) return messages.length > 0 ? messages[messages.length - 1].id : null;
  return i === 0 ? null : messages[i - 1].id;
}

/** The thread after a poll: what it held up to `after`, then what the poll returned. */
export function mergeFrom(messages: CopilotMessage[], after: string | null, fetched: CopilotMessage[]): CopilotMessage[] {
  if (after === null) return fetched;
  const i = messages.findIndex((m) => m.id === after);
  return i === -1 ? fetched : [...messages.slice(0, i + 1), ...fetched];
}

/**
 * How long until the next poll, or false to stop: nothing waits. While a message waits it polls every 4 s for its
 * first minute, then every 15 s, and from 5 minutes on every 60 s: with the brain PC off overnight an open page asks
 * 60 times an hour, not 900. After a failed poll it asks again every 60 s while a message still waits, so a page left
 * open while the API restarts recovers by itself (#843 review N1); the thread and the composer stay meanwhile.
 */
export function pollInterval(messages: CopilotMessage[], nowMs: number, failed: boolean): number | false {
  const first = messages.find(waiting);
  if (!first) return false;
  if (failed) return 60_000;
  const since = nowMs - Date.parse(first.created_at);
  if (!(since >= 0) || since < 60_000) return 4_000;
  return since < 300_000 ? 15_000 : 60_000;
}

/** Why the composer can't send this yet, or null when it can. Mirrors the server's limits. */
export function composerProblem(text: string, files: { name: string; size: number }[]): string | null {
  if (!text.trim() && files.length === 0) return "Write a message, attach a file, or both.";
  if ([...text.trim()].length > MAX_MESSAGE_CHARS) return `A message is at most ${MAX_MESSAGE_CHARS} characters.`;
  if (files.length > MAX_FILES) return "A message attaches one file: send each other file in a message of its own.";
  for (const f of files) {
    if (f.size > MAX_FILE_MB * 1024 * 1024) return `${f.name} is over ${MAX_FILE_MB} MB: split the export and send the parts.`;
    if (f.size === 0) return `${f.name} is empty.`;
  }
  return null;
}

/**
 * The question a link drafts in the composer (`?ask=`, the Plant Brief's "Ask the consultant"): trimmed, at most a
 * message's length, or null. The person reads it and sends it; nothing is sent by the link.
 */
export function askDraftOf(params: URLSearchParams): string | null {
  const ask = params.get("ask")?.trim();
  if (!ask) return null;
  return [...ask].slice(0, MAX_MESSAGE_CHARS).join("");
}

/** "in about 3 hours": when a refused person may ask again, from the server's Retry-After. */
export function whenAgain(seconds: number): string {
  if (seconds < 90) return "in a minute or so";
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `in about ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? "in about an hour" : `in about ${hours} hours`;
}

/**
 * Words as a sentence. The API writes its details as lowercase fragments with no full stop ("the consultant has 5 of your
 * messages to answer: wait for its replies"), and the page may add a sentence after one (#843 review N3). The first
 * letter is raised only when the first word is a plain word: a detail may start with a file's name, which keeps its
 * case ("mixer.csv: a file may be at most 64 MiB").
 */
export function asSentence(words: string): string {
  const s = words.trim();
  if (!s) return s;
  const first = s.split(/\s/, 1)[0];
  const raised = /^[a-z]+(?:'[a-z]+)?[,:;]?$/.test(first) ? s[0].toUpperCase() + s.slice(1) : s;
  return /[.!?]["')\]]*$/.test(raised) ? raised : `${raised}.`;
}

/**
 * What a person reads when the server turns something away: a sentence, and the server's code, shown small, only
 * where the sentence is the page's own (the server gave no words of its own, or it failed).
 */
export interface Refusal {
  text: string;
  code: string | null;
}

/** A 5xx: the server keeps its detail to itself (httpx.WriteError), so the page says it in its own words. */
export const PLATFORM_DOWN = "The platform could not answer just now. Try again in a minute.";

/**
 * A request that got no answer (offline, DNS, CORS, the web container being recreated). The API client marks fetch's
 * own rejection as a NetworkError; the browser's words in it differ by browser ("Failed to fetch", "NetworkError when
 * attempting to fetch resource.", "Load failed"), so the page says it in one sentence of its own (#866, #868 review).
 */
const PLATFORM_UNREACHABLE = "The platform could not be reached. Check the connection and try again.";

/**
 * Any other TypeError: the page's own code failed, often on an answer that did arrive (a null list), so it is not
 * blamed on the connection. It is logged once, however often the page renders it, so it is not hidden (#868 review).
 */
const PAGE_FAILED = "Something went wrong on this page. Reload and try again.";
const logged = new WeakSet<object>();

/**
 * What a person reads when the server refuses a message or a load fails: the server's own words as a sentence (never
 * "429 Too Many Requests"), and when they can ask again if it said. A copilot route that answers 404 is not mounted
 * here. A failure of the platform is said plainly, never as a bare code such as "query_failed", and so is a request
 * that got no answer (a NetworkError). A fault of the page's own is said as such, and logged.
 */
export function refusalOf(err: unknown, fallback: string): Refusal {
  if (err instanceof ApiClientError) {
    const body =
      typeof err.body === "object" && err.body !== null
        ? (err.body as { error?: unknown; detail?: unknown; message?: unknown })
        : null;
    const code = typeof body?.error === "string" && body.error ? body.error : null;
    if (err.status === 404) {
      return {
        text:
          code === "copilot_thread_not_found"
            ? "This conversation was not found: reload the page."
            : "Asking the consultant is not available here yet.",
        code: null,
      };
    }
    if (err.status >= 500) return { text: PLATFORM_DOWN, code: code ?? `HTTP ${err.status}` };
    const said = [body?.detail, body?.message].find((w): w is string => typeof w === "string" && w.trim() !== "");
    const words = asSentence(said ?? fallback);
    return {
      text: err.retryAfterS !== null ? `${words} You can ask again ${whenAgain(err.retryAfterS)}.` : words,
      code: said ? null : (code ?? `HTTP ${err.status}`),
    };
  }
  if (err instanceof NetworkError) return { text: PLATFORM_UNREACHABLE, code: null };
  if (err instanceof TypeError) {
    if (!logged.has(err)) {
      logged.add(err);
      console.error("imports conversation: an error of the page's own", err);
    }
    return { text: PAGE_FAILED, code: null };
  }
  return { text: asSentence(err instanceof Error && err.message ? err.message : fallback), code: null };
}

/** The sentence of refusalOf alone. */
export function refusalText(err: unknown, fallback: string): string {
  return refusalOf(err, fallback).text;
}

/** True when a load failed because the copilot's routes are not mounted (no COPILOT_WORKER_TOKEN on this API). */
export function copilotUnavailable(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 404;
}

/** Who may ask the brain: the server takes a message from an owner, an admin or a member; a viewer reads only. */
export function canAskTheBrain(role: string | null): boolean {
  return role === "owner" || role === "admin" || role === "member";
}

/** The thread the hub shows: the most recently active one, or none yet. */
export function newestThread(threads: CopilotThread[]): CopilotThread | null {
  if (threads.length === 0) return null;
  return [...threads].sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
}

/**
 * The thread the conversation shows (2026-09-28 page walk, P132): the one this page started, else the most recently
 * active one. A page's "Ask" link, or New conversation, asks for a fresh thread: none is shown until its first message
 * starts one, so the question is not asked inside an unrelated conversation (whose history the brain reads too).
 */
export function shownThread(s: { fresh: boolean; createdId: string | null; newest: string | null }): string | null {
  if (s.createdId !== null) return s.createdId;
  return s.fresh ? null : s.newest;
}

/** The longest thread title the page gives, in characters (the server keeps up to 200). */
export const THREAD_TITLE_MAX = 80;

/**
 * A new thread's title: what the page's link says the question is about, else the message's first line, else the
 * hub's "Imports".
 */
export function newThreadTitle(about: { label: string } | null, text: string): string {
  const line = (about?.label ?? "").trim() || (text.trim().split("\n")[0] ?? "").trim();
  if (line === "") return "Imports";
  const chars = Array.from(line); // by code point, so a cut never splits a character
  return chars.length > THREAD_TITLE_MAX ? `${chars.slice(0, THREAD_TITLE_MAX - 3).join("").trimEnd()}...` : line;
}

/** One of the conversation's loads (the person's threads, or the shown thread's messages) as it stands. */
export interface ConversationLoad {
  /** It holds data, from a load that succeeded at least once. */
  data: boolean;
  error: unknown;
}

/**
 * What the conversation shows above the composer, and whether the composer shows:
 *   - unavailable: the copilot's routes answer 404 here;
 *   - failed: a first load failed and there is nothing to show, so its error shows alone, with Try again;
 *   - loading: a first load runs;
 *   - thread: the thread (empty before the person's first message). A load that failed after it (a poll, while the
 *     API restarts) shows its error above the thread with Try again, and the thread and the composer stay (#843
 *     review N1). autoRetry: a message waits, so the page asks again every 60 s by itself (pollInterval).
 * The composer shows once the thread to post to is known, so a message sent while the list loads cannot open a second
 * thread; never where nothing loaded or the brain is not available; and only to someone who may ask.
 */
export type ConversationState =
  | { body: "unavailable"; composer: false }
  | { body: "failed"; error: Refusal; composer: false }
  | { body: "loading"; composer: boolean }
  | { body: "thread"; error: Refusal | null; autoRetry: boolean; composer: boolean };

/** A load that failed with the server saying nothing: the page's own words. */
export const LOAD_FAILED = "The server did not answer.";

export function conversationState(s: {
  threads: ConversationLoad;
  /** The thread shown, or null before the person's first (then there are no messages to load). */
  threadId: string | null;
  messages: ConversationLoad;
  /** The thread's messages as loaded so far. */
  items: CopilotMessage[];
  /** A first load runs. */
  loading: boolean;
  canAsk: boolean;
}): ConversationState {
  if (copilotUnavailable(s.threads.error) || copilotUnavailable(s.messages.error)) {
    return { body: "unavailable", composer: false };
  }
  const failure = s.threads.error || s.messages.error;
  const error = failure ? refusalOf(failure, LOAD_FAILED) : null;
  const loaded = s.threads.data && (s.threadId === null || s.messages.data);
  if (!loaded && error) return { body: "failed", error, composer: false };
  if (!loaded && s.loading) return { body: "loading", composer: s.canAsk && s.threads.data };
  return {
    body: "thread",
    error,
    autoRetry: error !== null && Boolean(s.messages.error) && awaitingBrain(s.items),
    composer: s.canAsk,
  };
}

/**
 * Sends a message to the thread shown or, before the person's first, to a new one: its id is kept at once, so a send
 * that fails is tried again on that thread instead of opening another. Returns the thread it went to.
 */
export async function sendToThread(
  threadId: string | null,
  post: (threadId: string) => Promise<unknown>,
  start: () => Promise<{ id: string }>,
  keep: (threadId: string) => void,
): Promise<string> {
  let id = threadId;
  if (!id) {
    id = (await start()).id;
    keep(id);
  }
  await post(id);
  return id;
}

/** A message as the person sent it: its words, trimmed as the server keeps them, and its file's name. */
export interface SentMessage {
  text: string;
  fileName: string | null;
}

/** What a send came to: it went (the composer clears), or what the composer says instead, keeping the message. */
export type SendOutcome = { kind: "sent" } | { kind: "error"; error: Refusal };

/** A send that got no answer may still have been kept: the page says only what it knows. */
const UNCONFIRMED = "We could not confirm your message was received. Check the conversation before sending it again.";

/** A file's name as the server keeps it (cleanUploadFilename): its last path part, control characters dropped, trimmed. */
function storedFileName(name: string): string {
  return name
    .slice(Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\")) + 1)
    .replace(/\p{Cc}/gu, "")
    .trim();
}

/**
 * Whether a kept message holds the words that were sent (#877 review). The textarea gives LF line breaks, but a
 * browser sends a form's text with CRLF (a lone CR too), and the server keeps the words as they came, trimmed at both
 * ends only (copilotText: strings.TrimSpace, which also takes U+0085). So line breaks compare as LF, and the ends are
 * trimmed as either side trims them.
 */
function sameWords(kept: string, sent: string): boolean {
  const canonical = (s: string) => s.replace(/\r\n?/g, "\n").replace(/^[\s\u0085]+|[\s\u0085]+$/g, "");
  return canonical(kept) === canonical(sent);
}

/**
 * The composer's outcome after a send got no answer, from the thread read again (#868 review). The server may have
 * kept the message, and only its answer been lost. So a person's message that was not in the thread before, with the
 * same words (sameWords) and the same file, means the message went: the composer clears, with no error. Otherwise the
 * page cannot tell, and it says so without claiming the message was not delivered; the composer keeps it. When the
 * thread had not loaded before the send (before is null), a match could be an earlier message with the same words, so
 * none counts. Nothing is ever sent again by itself.
 */
export function sendOutcomeAfterNetworkFailure(
  sent: SentMessage,
  before: CopilotMessage[] | null,
  thread: CopilotMessage[],
): SendOutcome {
  if (before !== null) {
    const known = new Set(before.map((m) => m.id));
    const file = sent.fileName === null ? null : storedFileName(sent.fileName);
    const went = thread.some(
      (m) =>
        m.role === "user" &&
        !known.has(m.id) &&
        sameWords(m.body, sent.text) &&
        (file === null ? m.attachments.length === 0 : m.attachments.length === 1 && m.attachments[0].filename === file),
    );
    if (went) return { kind: "sent" };
  }
  return { kind: "error", error: { text: UNCONFIRMED, code: null } };
}

/**
 * The container's send: what it came to (#868 review). A post that failed without an answer from the server may still
 * have been kept by it, so the thread is read again before the composer says anything
 * (sendOutcomeAfterNetworkFailure). That is any failure of the post but an ApiClientError (#877 review): fetch got no
 * answer (a NetworkError), or the connection dropped while the answer's body was read (the browser's own TypeError),
 * or the answer could not be read at all. An ApiClientError is the server's answer, so its words stand (refusalOf),
 * and a thread start that failed posted nothing. The message is never sent again by itself.
 */
export async function sendAndConfirm(s: {
  threadId: string | null;
  sent: SentMessage;
  /** The thread before the send: [] before the person's first, null while its messages had not loaded. */
  before: CopilotMessage[] | null;
  post: (threadId: string) => Promise<unknown>;
  start: () => Promise<{ id: string }>;
  keep: (threadId: string) => void;
  /** Reads the thread again, and gives it as the page then knows it. */
  recheck: (threadId: string) => Promise<CopilotMessage[]>;
}): Promise<SendOutcome> {
  const posted: { to: string | null } = { to: null };
  try {
    await sendToThread(
      s.threadId,
      (id) => {
        posted.to = id;
        return s.post(id);
      },
      s.start,
      s.keep,
    );
    return { kind: "sent" };
  } catch (err) {
    if (!(err instanceof ApiClientError) && posted.to !== null) {
      // Not fetch's own failure: logged, so it is not hidden (#868 review).
      if (!(err instanceof NetworkError)) console.error("imports conversation: a send lost its answer", err);
      const thread = await s.recheck(posted.to).catch((): CopilotMessage[] => []);
      return sendOutcomeAfterNetworkFailure(s.sent, s.before, thread);
    }
    return { kind: "error", error: refusalOf(err, "The message could not be sent.") };
  }
}

/**
 * What the composer holds once a message went: what was sent clears, and what the person typed or picked while it
 * went up stays (#843 review LOW-7).
 */
export function keptAfterSend<T>(sent: T, now: T, cleared: T): T {
  return now === sent ? cleared : now;
}

/** A message as the page sends it: the composer's (with its file, if one is picked), a starter, or an ask's answer. */
export interface OutgoingMessage {
  threadId: string;
  text: string;
  /** The composer's file; a starter and an answer have none. */
  file?: File | null;
  /** An ask's answer (sendAnswer). */
  answer?: CopilotAnswerBlock;
  /** What the chip says the question is about (lib/copilot/ask), or null. */
  context: CopilotAbout | null;
  /** The composer's message: without a context or a file it keeps the multipart form it always had. */
  composer?: boolean;
}

/** How a message goes up, and the context it carried (null when it carried none: the chip stays). */
export interface OutgoingPost {
  post: CopilotPost;
  carried: CopilotAbout | null;
}

/**
 * A tapped starter as the message it sends (2026-10-28): the starter's words, as they read, are the person's question,
 * with what the chip says it is about (null for none). It drafts nothing in the composer.
 */
export function starterMessage(words: string, context: CopilotAbout | null): Omit<OutgoingMessage, "threadId"> {
  return { text: words, context };
}

/**
 * How a message goes up (2026-09-28). The first message a person sends after following a page's "Ask" link carries
 * the chip's context as a block in the JSON form, after their answer when they answer an ask. A message with a file
 * goes in the multipart form, which carries no block: the chip keeps its context for the next message of words. The
 * composer's words alone, with no context, go in the multipart form as before; a starter or an answer, in the JSON
 * form. The container clears the chip once a message that carried it went (keptAfterSend).
 */
export function outgoingPost(m: OutgoingMessage): OutgoingPost {
  if (m.file) return { post: { threadId: m.threadId, text: m.text, files: [m.file] }, carried: null };
  if (m.composer && !m.context) return { post: { threadId: m.threadId, text: m.text, files: [] }, carried: null };
  const blocks: CopilotPersonBlock[] = [];
  if (m.answer) blocks.push(m.answer);
  if (m.context) blocks.push(contextBlockOf(m.context));
  const post: CopilotPost = blocks.length > 0 ? { threadId: m.threadId, text: m.text, blocks } : { threadId: m.threadId, text: m.text };
  return { post, carried: m.context };
}
